import { create } from 'zustand'
import { QuizSession } from '../types/analytics'
import { trackQuizAbandoned } from '../utils/tracking/tracking'
import { saveQuizSessionToSupabase } from '../lib/adminQueries'

interface QuizState {
  session: QuizSession | null
  initSession: (quizType: 'patient' | 'caregiver') => void
  movePage: (pageNum: number) => void
  goBackPage: (pageNum: number) => void
  recordAnswer: (pageNum: number, answer: string | string[]) => void
  completeQuiz: () => void
  abandonQuiz: (pageNum: number) => void
  getSessionId: () => string | null
}

const generateSessionId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

export const useQuizStore = create<QuizState>((set, get) => ({
  session: null,

  initSession: (quizType: 'patient' | 'caregiver') => {
    const sessionId = generateSessionId()
    const session: QuizSession = {
      sessionId,
      quizType,
      startedAt: Date.now(),
      currentPage: 0,
      totalPagesVisited: 1,
      completedPages: [0],
      status: 'in_progress',
      userAnswers: {},
      lastActivityAt: Date.now(),
    }
    set({ session })
    // Save to localStorage for persistence
    localStorage.setItem(`quiz_session_${sessionId}`, JSON.stringify(session))
  },

  movePage: (pageNum: number) => {
    set((state) => {
      if (!state.session) return state
      const session = { ...state.session }
      session.currentPage = pageNum
      session.lastActivityAt = Date.now()
      if (!session.completedPages.includes(pageNum)) {
        session.totalPagesVisited++
        session.completedPages.push(pageNum)
      }
      localStorage.setItem(`quiz_session_${session.sessionId}`, JSON.stringify(session))
      return { session }
    })
  },

  goBackPage: (pageNum: number) => {
    set((state) => {
      if (!state.session) return state
      const session = { ...state.session }
      session.currentPage = pageNum
      session.lastActivityAt = Date.now()
      localStorage.setItem(`quiz_session_${session.sessionId}`, JSON.stringify(session))
      return { session }
    })
  },

  recordAnswer: (pageNum: number, answer: string | string[]) => {
    set((state) => {
      if (!state.session) return state
      const session = { ...state.session }
      session.userAnswers[pageNum] = answer
      session.lastActivityAt = Date.now()
      localStorage.setItem(`quiz_session_${session.sessionId}`, JSON.stringify(session))
      return { session }
    })
  },

  completeQuiz: () => {
    set((state) => {
      if (!state.session) return state
      const session = { ...state.session }
      session.status = 'completed'
      session.endedAt = Date.now()
      localStorage.setItem(`quiz_session_${session.sessionId}`, JSON.stringify(session))
      // Save to Supabase
      saveQuizSessionToSupabase(session).catch(console.error)
      // Send to backend
      fetch('/api/quiz-analytics/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(session),
      }).catch(console.error)
      return { session }
    })
  },

  abandonQuiz: (pageNum: number) => {
    set((state) => {
      if (!state.session) return state
      const session = { ...state.session }
      session.status = 'abandoned'
      session.abandonedAt = Date.now()
      session.abandonedAtPage = pageNum
      localStorage.setItem(`quiz_session_${session.sessionId}`, JSON.stringify(session))

      // Track abandonment
      const totalTimeMs = (session.abandonedAt || Date.now()) - session.startedAt;
      trackQuizAbandoned(session.quizType, pageNum, totalTimeMs);

      // Save to Supabase
      saveQuizSessionToSupabase(session).catch(console.error)

      // Send to backend
      fetch('/api/quiz-analytics/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(session),
      }).catch(console.error)
      return { session }
    })
  },

  getSessionId: () => {
    return get().session?.sessionId || null
  },
}))
